import { Container, Typography } from '@cleartrip/ct-design-components'
import Image from 'next/image'
import React, { RefObject } from 'react'

import BtnMediumTick from '@/assets/icons/BtnMediumTick'
import CtUpgrade from '@/assets/logo/CtUpgrade'
import ButtonWithDisabledClick from '@/components/common/ButtonWithDisabledClick'
import { formatCurrency } from '@/utils'

import FareCouponCallout from './FareCouponCallout'
import FarePreAppliedCallout from './FarePreAppliedCallout'
import PtcBadge from '@/components/common/ptc/PtcBadge'

interface FareDetailsProps {
    isSelected: boolean
    selectFare: () => void
    displayTitle: string
    calloutNode?: React.ReactNode
    displaySubTitle?: string
    displayPrice: number
    strikeOffPrice: number
    showPerAdultPricing: boolean
    couponCallout: string
    slasherCouponCode?: string
    isSubtitlePresent: boolean
    buttonLoader?: boolean
    isFFFlow ?: boolean
    isCouponPresentInAnyFare?: boolean
    showPtcTag?: boolean
    titleLogo?: string
    isDisabledFareCard?: boolean
    isCTBrandedFareCard?: boolean
    onDisabledFareClick?: () => void
    benefitRef?: RefObject<HTMLDivElement>
    smbVasCouponCallout?: string
    ctUpgradeText?: string
}
const FareDetails = ({
    displayTitle,
    displaySubTitle,
    displayPrice,
    strikeOffPrice,
    isSelected,
    showPerAdultPricing,
    selectFare,
    couponCallout,
    slasherCouponCode,
    isSubtitlePresent,
    buttonLoader,
    isFFFlow,
    isCouponPresentInAnyFare,
    showPtcTag = false,
    titleLogo,
    isDisabledFareCard,
    isCTBrandedFareCard,
    onDisabledFareClick,
    calloutNode,
    smbVasCouponCallout = '',
    ctUpgradeText = ''
}: FareDetailsProps) => {
    const buttonVariant = isSelected ? 'contained' : 'outline'
    const buttonText = isSelected ? 'Selected' : 'Select'
    const hasBenefitCallout = !!(smbVasCouponCallout || ctUpgradeText)
    return (
        <Container className="mb-3">
            <Container className="">
                <Container className="flex flex-middle flex-cg-1">
                    {titleLogo && <Image alt="title logo" className='w-50p' height={20} src={titleLogo} width={92} />}
                    {!isCTBrandedFareCard && !titleLogo ? (
                        <Typography className="ws-nowrap t-truncate" variant="B2">
                            {displayTitle}
                        </Typography>
                    ) : null}
                    {showPtcTag && (
                        <Container className="ml-1">
                            <PtcBadge/>
                        </Container>
                    )}
                </Container>
                {displaySubTitle && !isFFFlow ? (
                    <Typography className="ws-nowrap t-truncate" variant="B4">
                        ({displaySubTitle})
                    </Typography>
                ) : isSubtitlePresent && !isFFFlow ? (
                    <Container height="14px" />
                ) : (
                    ''
                )}
            </Container>

            <Container className="mt-1 flex flex-baseline">
                <Typography variant="HM2">{formatCurrency(displayPrice)}</Typography>
                {strikeOffPrice > 0 ? (
                    <Typography className="ml-1" color="subheading" isStriked={true} variant="B3">
                        {formatCurrency(strikeOffPrice)}
                    </Typography>
                ) : (
                    ''
                )}
            </Container>
            {showPerAdultPricing ? (
                <Typography color="subheading" variant="B3">
                    per adult
                </Typography>
            ) : (
                ''
            )}
            <Container className="flex flex-column">
                {slasherCouponCode ? <FarePreAppliedCallout slasherCouponCode={slasherCouponCode} /> : null}
                {calloutNode ?? (
                    <FareCouponCallout
                        compact={hasBenefitCallout}
                        couponCallout={couponCallout}
                        isCouponPresentInAnyFare={!hasBenefitCallout && !!isCouponPresentInAnyFare}
                    />
                )}
                {ctUpgradeText ? (
                    <Container className="flex flex-middle flex-gap-1 flex-nowrap" css={{ marginTop: '2px' }}>
                        <CtUpgrade />
                        <Typography className="ws-nowrap" color="primary" variant="B4">
                            {ctUpgradeText}
                        </Typography>
                    </Container>
                ) : smbVasCouponCallout ? (
                    <Typography
                        className="t-truncate ta-right"
                        colorCode="#1A1A1A"
                        css={{ marginTop: '2px', width: '100%', minWidth: 0 }}
                        lineClamp={2}
                        variant="B4"
                    >
                        {smbVasCouponCallout}
                    </Typography>
                ) : null}
                {!hasBenefitCallout && !slasherCouponCode && isCouponPresentInAnyFare ? (
                    <Container css={{ height: '16px' }} />
                ) : null}
            </Container>
            <Container className="py-3">
                <ButtonWithDisabledClick
                    isFullWidth
                    disabled={isDisabledFareCard}
                    LeftIcon={<BtnMediumTick color={isDisabledFareCard ? '#B3B3B3' : 'white'} />}
                    loading={buttonLoader}
                    showLeftIcon={isSelected}
                    size="medium"
                    variant={buttonVariant}
                    onClick={selectFare}
                    onDisabledClick={onDisabledFareClick}
                >
                    {buttonText}
                </ButtonWithDisabledClick>
            </Container>
        </Container>
    )
}

export default FareDetails
